import { Link } from 'react-router-dom';
import { getExam, getExamTypesForLanguage } from '@examflow/core';
import {
  ArrowRight,
  LogOut,
  TicketCheck,
  UserRound,
} from 'lucide-react';
import { BrandLogo, Button, LoadingSpinner, buttonVariants, cn } from '@examflow/ui';
import { BeianFooter } from '../components/BeianFooter';
import { useAuth } from '../features/auth/AuthContext';
import { SyncStatusIndicator } from '../components/sync/SyncStatusIndicator';
import { useSyncStatus } from '../hooks/useSyncStatus';
import { buildWorkspaceHrefForExam } from '../lib/userPreferences';
import type { ActiveEntitlements, User } from '../lib/api';
import { usePlatformSettings } from '../features/platform/PlatformSettingsContext';

interface ProfileViewProps {
  user: User;
  entitlements: ActiveEntitlements | null;
  onLogout: () => void;
  siteName?: string;
  supportEmail?: string;
  supportDocsUrl?: string;
}

interface OpenedExamSpace {
  id: string;
  name: string;
  description: string;
  expiresAt: string | null;
  workspaceHref: string;
}

function formatDate(value: string | null): string {
  if (!value) return '长期有效';

  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));
}

function resolveExamSpaceName(examType: string): string {
  const normalized = examType.trim().toLowerCase();
  return getExam(normalized)?.exam.name ?? normalized.toUpperCase();
}

function accountStatusLabel(status: User['status']): string {
  switch (status) {
    case 'active':
      return '正常';
    case 'disabled':
      return '已停用';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function addOpenedExamSpace(
  spaces: Map<string, OpenedExamSpace>,
  examType: string,
  expiresAt: string | null
) {
  const normalized = examType.trim().toLowerCase();
  if (!normalized || spaces.has(normalized)) return;

  const examInfo = getExam(normalized);
  spaces.set(normalized, {
    id: normalized,
    name: resolveExamSpaceName(normalized),
    description: examInfo
      ? `${examInfo.language.name} · ${examInfo.exam.fullName ?? examInfo.exam.name}`
      : '考试训练空间',
    expiresAt,
    workspaceHref: buildWorkspaceHrefForExam(normalized),
  });
}

function buildOpenedExamSpaces(entitlements: ActiveEntitlements | null): OpenedExamSpace[] {
  const spaces = new Map<string, OpenedExamSpace>();

  for (const grant of entitlements?.grants ?? []) {
    for (const examType of grant.examTypes) {
      addOpenedExamSpace(spaces, examType, grant.expiresAt);
    }

    for (const languageId of grant.languageIds ?? []) {
      for (const examType of getExamTypesForLanguage(languageId.trim().toLowerCase())) {
        addOpenedExamSpace(spaces, examType, grant.expiresAt);
      }
    }
  }

  for (const examType of entitlements?.examTypes ?? []) {
    addOpenedExamSpace(spaces, examType, null);
  }

  for (const languageId of entitlements?.languageIds ?? []) {
    for (const examType of getExamTypesForLanguage(languageId.trim().toLowerCase())) {
      addOpenedExamSpace(spaces, examType, null);
    }
  }

  return Array.from(spaces.values());
}

export function ProfileView({
  user,
  entitlements,
  onLogout,
  siteName = 'ExamFlow',
  supportEmail = '',
  supportDocsUrl = '',
}: ProfileViewProps) {
  const openedSpaces = buildOpenedExamSpaces(entitlements);
  const displayName = user.name || user.email.split('@')[0];
  const { lastSyncTime, triggerSync, isSyncing } = useSyncStatus();
  const statusLabel = accountStatusLabel(user.status);

  function formatLastSync(timestamp: number | null): string {
    if (!timestamp) return '从未同步';
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes} 分钟前`;
    if (hours < 24) return `${hours} 小时前`;
    return new Date(timestamp).toLocaleDateString('zh-CN');
  }

  return (
    <main className="min-h-screen bg-bg-page text-ink-primary">
      <header className="border-b border-sage-100 bg-bg-surface">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="inline-flex items-center" aria-label={`${siteName} 首页`}>
            <BrandLogo variant="full" size={32} alt={siteName} />
          </Link>
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.34fr)]">
        <div className="space-y-6">
          <div className="border border-sage-100 bg-bg-surface p-7">
            <h1 className="text-3xl font-black tracking-normal">个人中心</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-ink-secondary">
              查看登录身份与已开通的考试空间。训练进度继续在考试空间里完成。
            </p>
          </div>

          <section className="border border-sage-100 bg-bg-surface p-6">
            <div className="flex items-center gap-3">
              <UserRound className="h-5 w-5 text-accent" />
              <h2 className="text-lg font-black">登录身份</h2>
            </div>
            <div className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-xs font-bold text-ink-muted">显示名称</p>
                <p className="mt-1 font-semibold text-ink-primary">{displayName}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-ink-muted">登录邮箱</p>
                <p className="mt-1 font-semibold text-ink-primary">{user.email}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-ink-muted">账号状态</p>
                <p
                  className={cn(
                    'mt-1 font-semibold',
                    user.status === 'active' ? 'text-ink-primary' : 'text-state-warning'
                  )}
                >
                  {statusLabel}
                </p>
              </div>
            </div>
          </section>

          <section className="border border-sage-100 bg-bg-surface p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <TicketCheck className="h-5 w-5 text-accent" />
                <h2 className="text-lg font-black">已开通的考试空间</h2>
              </div>
              <Link
                to={`/redeem?redirect=${encodeURIComponent('/profile')}`}
                className="text-sm font-semibold text-primary hover:underline"
              >
                兑换开通
              </Link>
            </div>

            {openedSpaces.length > 0 ? (
              <div className="mt-6 grid gap-4">
                {openedSpaces.map((space) => (
                  <div
                    key={space.id}
                    className="flex flex-col justify-between gap-4 border border-sage-100 px-5 py-4 md:flex-row md:items-center"
                  >
                    <div>
                      <p className="text-xs font-bold text-accent">当前可进入</p>
                      <h3 className="mt-2 text-xl font-black">{space.name}</h3>
                      <p className="mt-2 text-sm text-ink-secondary">{space.description}</p>
                      <p className="mt-2 text-xs font-semibold text-ink-muted">
                        有效期：{formatDate(space.expiresAt)}
                      </p>
                    </div>
                    <Link
                      to={space.workspaceHref}
                      className={cn(buttonVariants({ variant: 'primary' }), 'rounded-sm px-5')}
                    >
                      进入考试空间
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-6 border border-dashed border-sage-100 bg-bg-muted p-5">
                <h3 className="font-black">暂无已开通的考试空间</h3>
                <p className="mt-2 text-sm leading-6 text-ink-secondary">
                  使用兑换码开通后，这里会显示可进入的考试空间。
                </p>
                <Link
                  to={`/redeem?redirect=${encodeURIComponent('/profile')}`}
                  className={cn(buttonVariants({ variant: 'outline' }), 'mt-5 rounded-sm')}
                >
                  兑换考试空间
                </Link>
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="border border-sage-100 bg-bg-surface p-6">
            <h2 className="text-lg font-black">账号操作</h2>
            <p className="mt-3 text-sm leading-6 text-ink-secondary">
              退出后再次进入考试空间需要重新登录。
            </p>
            <Button
              type="button"
              variant="outline"
              fullWidth
              className="mt-5 rounded-sm"
              onClick={onLogout}
            >
              <LogOut className="h-4 w-4" />
              退出登录
            </Button>
          </section>

          <details className="border border-sage-100 bg-bg-surface">
            <summary className="cursor-pointer select-none px-6 py-4 text-sm font-semibold text-ink-secondary">
              设备同步（可选）
            </summary>
            <div className="space-y-3 border-t border-sage-100 px-6 py-4">
              <p className="text-xs leading-5 text-ink-muted">
                将本机草稿与服务器对齐。日常学习不必手动操作。
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-ink-secondary">{formatLastSync(lastSyncTime)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 rounded-sm px-2 text-xs"
                  onClick={triggerSync}
                  disabled={isSyncing}
                >
                  {isSyncing ? '同步中...' : '立即同步'}
                </Button>
              </div>
              <SyncStatusIndicator />
            </div>
          </details>

          {(supportEmail || supportDocsUrl) && (
            <section className="border border-sage-100 bg-bg-surface p-6">
              <h2 className="text-lg font-black">支持联系</h2>
              <div className="mt-3 space-y-2 text-sm text-ink-secondary">
                {supportEmail ? (
                  <p>
                    邮箱：{' '}
                    <a className="underline" href={`mailto:${supportEmail}`}>
                      {supportEmail}
                    </a>
                  </p>
                ) : null}
                {supportDocsUrl ? (
                  <p>
                    <a
                      className="underline"
                      href={supportDocsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      帮助文档
                    </a>
                  </p>
                ) : null}
              </div>
            </section>
          )}
        </aside>
      </section>

      <BeianFooter siteName={siteName} />
    </main>
  );
}

export default function ProfilePage() {
  const { user, entitlements, logout, loading } = useAuth();
  const { settings } = usePlatformSettings();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <ProfileView
      user={user}
      entitlements={entitlements}
      siteName={settings.siteName}
      supportEmail={settings.supportEmail}
      supportDocsUrl={settings.supportDocsUrl}
      onLogout={() => {
        logout();
      }}
    />
  );
}
